import React, { useEffect, useMemo, useState } from 'react';
import '../css/accounting.css';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

function Archive() {
  const user = localStorage.getItem('loggedInUsername');
  const currentMonth = new Date().toISOString().slice(0, 7);
  const currentYear = new Date().getFullYear();

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [months, setMonths] = useState([]);
  const [archiveBalances, setArchiveBalances] = useState([]);
  const [archiveTransactions, setArchiveTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedArchiveAccountIds, setSelectedArchiveAccountIds] = useState([]);
  const [hasInitializedArchiveAccountSelection, setHasInitializedArchiveAccountSelection] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const [balancesFile, setBalancesFile] = useState(null);
  const [balancesYear, setBalancesYear] = useState(currentYear);
  const [balancesImportStatus, setBalancesImportStatus] = useState('');

  const [transactionsFile, setTransactionsFile] = useState(null);
  const [transactionsYear, setTransactionsYear] = useState(currentYear);
  const [transactionsImportStatus, setTransactionsImportStatus] = useState('');

  const getToken = () => localStorage.getItem('accessToken');

  const loadArchiveMonths = async (token) => {
    const response = await fetch('/api/accounting/archive/months', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load archive months');
    }

    return response.json();
  };

  const loadArchiveMonth = async (token, monthKey) => {
    const response = await fetch('/api/accounting/archive/month', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ user, month_key: monthKey })
    });

    if (!response.ok) {
      throw new Error('Failed to load archive month');
    }

    return response.json();
  };

  const loadAccounts = async (token) => {
    const response = await fetch('/api/accounting/accounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load accounts');
    }

    return response.json();
  };

  const refreshArchiveView = async (monthKey) => {
    const token = getToken();
    if (!token) {
      return;
    }

    try {
      setIsBusy(true);
      setStatusMessage('');
      const [monthsData, archiveData, accountsData] = await Promise.all([
        loadArchiveMonths(token),
        loadArchiveMonth(token, monthKey),
        loadAccounts(token)
      ]);

      const monthList = Array.isArray(monthsData.months) ? monthsData.months : [];
      const accountsList = Array.isArray(accountsData) ? accountsData : [];
      setMonths(monthList);
      setAccounts(accountsList);
      setSelectedArchiveAccountIds((previousSelected) => {
        const allAccountIds = accountsList
          .map((account) => Number(account.id))
          .filter((accountId) => Number.isFinite(accountId));

        if (!hasInitializedArchiveAccountSelection) {
          return allAccountIds;
        }

        const previousSet = new Set((previousSelected || []).map((accountId) => Number(accountId)));
        return allAccountIds.filter((accountId) => previousSet.has(accountId));
      });
      if (!hasInitializedArchiveAccountSelection) {
        setHasInitializedArchiveAccountSelection(true);
      }
      setArchiveBalances(Array.isArray(archiveData.balances) ? archiveData.balances : []);
      setArchiveTransactions(Array.isArray(archiveData.transactions) ? archiveData.transactions : []);
      if (monthList.length === 0) {
        setStatusMessage('No archive snapshots found yet.');
      }
    } catch (error) {
      setAccounts([]);
      setSelectedArchiveAccountIds([]);
      setArchiveBalances([]);
      setArchiveTransactions([]);
      setStatusMessage('Unable to load archive data.');
    } finally {
      setIsBusy(false);
    }
  };

  useEffect(() => {
    refreshArchiveView(selectedMonth);
  }, [selectedMonth]);

  const handleRearchive = async () => {
    const token = getToken();
    if (!token || !selectedMonth) {
      return;
    }

    if (selectedArchiveAccountIds.length === 0) {
      setStatusMessage('Select at least one account to re-archive.');
      return;
    }

    try {
      setIsBusy(true);
      setStatusMessage('Re-archiving selected month...');
      const response = await fetch('/api/accounting/archive/rearchive', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          month_key: selectedMonth,
          account_ids: selectedArchiveAccountIds
        })
      });

      if (!response.ok) {
        throw new Error('Failed to re-archive month');
      }

      await refreshArchiveView(selectedMonth);
      setStatusMessage(`Re-archive completed for ${selectedMonth}.`);
    } catch (error) {
      setStatusMessage('Unable to re-archive selected month.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleArchiveAccountToggle = (accountId) => {
    const normalizedId = Number(accountId);
    if (!Number.isFinite(normalizedId)) {
      return;
    }

    setSelectedArchiveAccountIds((previousSelected) => {
      const existing = new Set((previousSelected || []).map((value) => Number(value)));
      if (existing.has(normalizedId)) {
        existing.delete(normalizedId);
      } else {
        existing.add(normalizedId);
      }
      return Array.from(existing);
    });
  };

  const handleImportBalances = async () => {
    const token = getToken();
    if (!token || !balancesFile) {
      setBalancesImportStatus('Select a balances CSV file first.');
      return;
    }

    try {
      setBalancesImportStatus('Importing balances archive...');
      const formData = new FormData();
      formData.append('user', user);
      formData.append('year', String(balancesYear));
      formData.append('file', balancesFile);

      const response = await fetch('/api/accounting/archive/import/balances', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        setBalancesImportStatus(data.error || 'Balance archive import failed.');
        return;
      }

      const unmapped = Array.isArray(data.unmapped_columns) && data.unmapped_columns.length
        ? ` Unmapped columns: ${data.unmapped_columns.join(', ')}.`
        : '';

      setBalancesImportStatus(`Archived ${data.balances_archived || 0} balances across ${data.archived_months || 0} month(s).${unmapped}`);
      await refreshArchiveView(selectedMonth);
    } catch (error) {
      setBalancesImportStatus('Balance archive import failed.');
    }
  };

  const handleImportTransactions = async () => {
    const token = getToken();
    if (!token || !transactionsFile) {
      setTransactionsImportStatus('Select a transactions CSV file first.');
      return;
    }

    try {
      setTransactionsImportStatus('Importing transactions archive...');
      const formData = new FormData();
      formData.append('user', user);
      formData.append('year', String(transactionsYear));
      formData.append('entry_type', 'credit');
      formData.append('file', transactionsFile);

      const response = await fetch('/api/accounting/archive/import/transactions', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        setTransactionsImportStatus(data.error || 'Transaction archive import failed.');
        return;
      }

      setTransactionsImportStatus(`Archived ${data.archived || 0} transaction record(s).`);
      await refreshArchiveView(selectedMonth);
    } catch (error) {
      setTransactionsImportStatus('Transaction archive import failed.');
    }
  };

  const sortedMonths = useMemo(() => {
    const base = Array.isArray(months) ? months : [];
    const withSelection = base.includes(selectedMonth) ? base : [selectedMonth, ...base];
    return [...new Set(withSelection)].sort((a, b) => String(b).localeCompare(String(a)));
  }, [months, selectedMonth]);

  const sortedAccounts = useMemo(() => {
    const accountList = Array.isArray(accounts) ? accounts : [];
    return [...accountList].sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));
  }, [accounts]);


  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Archive</h2>
            <p className="section-subtitle">Read-only historical snapshot ingestion and month re-archive.</p>
          </div>
          <label>
            Month
            <input
              type="month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
            />
          </label>
        </div>

        <section className="panel">
          <h3>Archive Actions</h3>
          <p className="panel-sub">Import historical CSVs or rebuild a month from current forecast and transactions for selected accounts.</p>
          {sortedAccounts.length === 0 ? (
            <div className="empty-state">No accounts available.</div>
          ) : (
            <div className="form-grid" style={{ marginBottom: '1rem' }}>
              {sortedAccounts.map((account) => {
                const accountId = Number(account.id);
                const isChecked = selectedArchiveAccountIds.includes(accountId);
                return (
                  <label className="inline-switch" key={`archive-account-${account.id}`}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleArchiveAccountToggle(account.id)}
                      disabled={isBusy}
                    />
                    <span>{account.name}</span>
                  </label>
                );
              })}
            </div>
          )}
          <div className="button-group" style={{ justifyContent: 'flex-start' }}>
            <button
              className="login-button"
              type="button"
              onClick={handleRearchive}
              disabled={isBusy || selectedArchiveAccountIds.length === 0}
            >
              Re-archive selected month
            </button>
          </div>
          {statusMessage && <div className="empty-state">{statusMessage}</div>}
        </section>

        <section className="panel">
          <h3>Import Historical Balances</h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <label>
              Year
              <input
                type="number"
                min="2000"
                max="2100"
                value={balancesYear}
                onChange={(event) => setBalancesYear(Number(event.target.value))}
              />
            </label>
            <label>
              Balances CSV
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => setBalancesFile(event.target.files && event.target.files[0] ? event.target.files[0] : null)}
              />
            </label>
          </div>
          <div className="button-group" style={{ justifyContent: 'flex-start' }}>
            <button className="login-button" type="button" onClick={handleImportBalances} disabled={isBusy}>
              Import balances archive
            </button>
          </div>
          {balancesImportStatus && <div className="empty-state">{balancesImportStatus}</div>}
        </section>

        <section className="panel">
          <h3>Import Historical Transactions</h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <label>
              Year
              <input
                type="number"
                min="2000"
                max="2100"
                value={transactionsYear}
                onChange={(event) => setTransactionsYear(Number(event.target.value))}
              />
            </label>
            <label>
              Transactions CSV
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => setTransactionsFile(event.target.files && event.target.files[0] ? event.target.files[0] : null)}
              />
            </label>
          </div>
          <div className="button-group" style={{ justifyContent: 'flex-start' }}>
            <button className="login-button" type="button" onClick={handleImportTransactions} disabled={isBusy}>
              Import transactions archive
            </button>
          </div>
          {transactionsImportStatus && <div className="empty-state">{transactionsImportStatus}</div>}
        </section>

        <section className="panel">
          <h3>Archived Month Data</h3>
          {isBusy && <div className="loading-state">Loading archive data...</div>}
          {!isBusy && (
            <>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: '1rem' }}>
                <label>
                  Archived month list
                  <select value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)}>
                    {sortedMonths.map((monthKey) => (
                      <option key={monthKey} value={monthKey}>{monthKey}</option>
                    ))}
                  </select>
                </label>
              </div>

              <h4 style={{ margin: '0 0 0.75rem' }}>Account Balances</h4>
              {archiveBalances.length === 0 ? (
                <div className="empty-state">No archived balances for this month.</div>
              ) : (
                <table className="table" style={{ marginBottom: '1.5rem' }}>
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Balance</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archiveBalances.map((row) => (
                      <tr key={`${row.month_key}-${row.account_name}-${row.id}`}>
                        <td>{row.account_name}</td>
                        <td>{formatCurrency(row.balance)}</td>
                        <td>{row.archive_source || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h4 style={{ margin: '0 0 0.75rem' }}>Transactions</h4>
              {archiveTransactions.length === 0 ? (
                <div className="empty-state">No archived transactions for this month.</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archiveTransactions.map((row) => (
                      <tr key={`${row.month_key}-${row.id}`}>
                        <td>{new Date((row.occurred_at || 0) * 1000).toLocaleDateString()}</td>
                        <td>{row.description || '-'}</td>
                        <td>{row.entry_type || '-'}</td>
                        <td>{formatCurrency(row.amount)}</td>
                        <td>{row.source || row.archive_source || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </section>
      </section>
    </div>
  );
}

export default Archive;
